import React,{useMemo,useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PlatformBadges from './PlatformBadges';
import './Calendar.css';

const {
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiPlus
}=FiIcons;

const weekdays=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function toDateKey(date) {
  const year=date.getFullYear();
  const month=String(date.getMonth()+1).padStart(2,'0');
  const day=String(date.getDate()).padStart(2,'0');
  return `${year}-${month}-${day}`;
}

function getMonthDays(monthDate) {
  const first=new Date(
    monthDate.getFullYear(),
    monthDate.getMonth(),
    1
  );
  const start=new Date(first);
  start.setDate(first.getDate()-first.getDay());

  return Array.from({length:42},(_,index)=> {
    const day=new Date(start);
    day.setDate(start.getDate()+index);
    return day;
  });
}

function formatMonth(date) {
  return date.toLocaleDateString('en-US',{
    month:'long',
    year:'numeric'
  });
}

function CalendarScreen({
  posts,
  brand,
  loading,
  error,
  onOpen,
  onSchedule
}) {
  const [monthDate,setMonthDate]=useState(new Date());
  const [selectedDate,setSelectedDate]=useState(null);
  const [draggingId,setDraggingId]=useState(null);
  const [scheduleError,setScheduleError]=useState('');
  const [scheduling,setScheduling]=useState(false);
  const todayKey=toDateKey(new Date());
  const days=useMemo(()=> getMonthDays(monthDate),[monthDate]);
  const unscheduled=posts.filter(
    (post)=> post.status==='approved' && !post.scheduledDate
  );

  const postsForDay=(date)=> posts.filter((post)=> {
    const dateKey=toDateKey(date);

    return post.scheduledDate===dateKey
      || (post.status==='posted' && post.postedDate===dateKey);
  });

  const schedulePost=async (postId,dateKey)=> {
    setScheduleError('');
    setScheduling(true);

    const {error:scheduleFailure}=await onSchedule(postId,dateKey);

    setScheduling(false);

    if (scheduleFailure) {
      setScheduleError(
        scheduleFailure.message || 'Failed to schedule post.'
      );
      return;
    }

    setSelectedDate(null);
    setDraggingId(null);
  };

  const handleDayClick=(date)=> {
    const dateKey=toDateKey(date);

    if (dateKey>=todayKey) {
      setSelectedDate(dateKey);
    }
  };

  return (
    <main className="page calendar-page">
      <header className="calendar-header">
        <div>
          <span className="eyebrow">{brand.name}</span>
          <h1>Content calendar</h1>
          <p>
            See your posting rhythm, then make the next idea easier to place.
          </p>
        </div>
        <div className="calendar-mark">
          <SafeIcon icon={FiCalendar} />
        </div>
      </header>

      {(error || scheduleError) && (
        <p className="calendar-error" role="alert">
          {error || scheduleError}
        </p>
      )}

      <section className="calendar-toolbar">
        <div className="month-controls">
          <button onClick={()=> setMonthDate(new Date())}>
            Today
          </button>
          <button
            aria-label="Previous month"
            onClick={()=> setMonthDate(new Date(
              monthDate.getFullYear(),
              monthDate.getMonth()-1,
              1
            ))}
          >
            <SafeIcon icon={FiChevronLeft} />
          </button>
          <h2>{formatMonth(monthDate)}</h2>
          <button
            aria-label="Next month"
            onClick={()=> setMonthDate(new Date(
              monthDate.getFullYear(),
              monthDate.getMonth()+1,
              1
            ))}
          >
            <SafeIcon icon={FiChevronRight} />
          </button>
        </div>

        <div className="calendar-legend">
          <span><i className="legend-posted" /> Posted</span>
          <span><i className="legend-planned" /> Planned</span>
        </div>
      </section>

      {loading ? (
        <div className="calendar-loading">
          <p>Loading calendar…</p>
        </div>
      ) : (
        <section className="calendar-layout">
          <div className="calendar-board">
            <div className="weekday-row">
              {weekdays.map((day)=> <span key={day}>{day}</span>)}
            </div>

            <div className="calendar-grid">
              {days.map((date)=> {
                const dateKey=toDateKey(date);
                const inMonth=date.getMonth()===monthDate.getMonth();
                const dayPosts=postsForDay(date);

                return (
                  <div
                    className={`calendar-cell ${inMonth ? '' : 'outside-month'} ${dateKey===todayKey ? 'today' : ''}`}
                    key={dateKey}
                    onClick={()=> handleDayClick(date)}
                    onDragOver={(event)=> event.preventDefault()}
                    onDrop={()=> (
                      draggingId && !scheduling && schedulePost(draggingId,dateKey)
                    )}
                  >
                    <span className="date-number">{date.getDate()}</span>
                    <div className="day-posts">
                      {dayPosts.map((post)=> (
                        <button
                          className={`calendar-post ${post.status==='posted' ? 'posted' : 'planned'}`}
                          key={post.id}
                          onClick={(event)=> {
                            event.stopPropagation();
                            onOpen(post);
                          }}
                        >
                          <span>{post.headline}</span>
                          <PlatformBadges
                            platforms={post.publishedPlatforms}
                            compact
                          />
                        </button>
                      ))}
                    </div>
                    {inMonth && dateKey>=todayKey && (
                      <span className="cell-add">
                        <SafeIcon icon={FiPlus} />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <aside className="unscheduled-panel">
            <div className="unscheduled-heading">
              <div>
                <span className="eyebrow">Ready to place</span>
                <h2>Unscheduled</h2>
              </div>
              <span className="unscheduled-count">
                {unscheduled.length}
              </span>
            </div>

            <p>
              Drag an approved post onto a future date, or click a day to
              choose one.
            </p>

            <div className="unscheduled-list">
              {unscheduled.length ? unscheduled.map((post)=> (
                <motion.button
                  className="unscheduled-post"
                  key={post.id}
                  draggable={!scheduling}
                  onDragStart={()=> setDraggingId(post.id)}
                  onClick={()=> setSelectedDate(todayKey)}
                >
                  <span className="post-status">
                    <SafeIcon icon={FiClock} />
                    Approved
                  </span>
                  <strong>{post.headline}</strong>
                  <small>Drag to schedule</small>
                </motion.button>
              )) : (
                <div className="calendar-empty">
                  All approved posts have a place on the calendar.
                </div>
              )}
            </div>
          </aside>
        </section>
      )}

      {selectedDate && (
        <SchedulePicker
          date={selectedDate}
          posts={unscheduled}
          scheduling={scheduling}
          onClose={()=> !scheduling && setSelectedDate(null)}
          onSchedule={schedulePost}
        />
      )}
    </main>
  );
}

function SchedulePicker({
  date,
  posts,
  scheduling,
  onClose,
  onSchedule
}) {
  const formatted=new Date(`${date}T12:00:00`).toLocaleDateString(
    'en-US',
    {month:'long',day:'numeric',year:'numeric'}
  );

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <motion.div
        className="schedule-modal"
        onMouseDown={(event)=> event.stopPropagation()}
        initial={{opacity:0,scale:0.96}}
        animate={{opacity:1,scale:1}}
      >
        <span className="eyebrow">Plan a post</span>
        <h2>{formatted}</h2>
        <p>Choose an approved post to place on this date.</p>
        <div className="picker-list">
          {posts.length ? posts.map((post)=> (
            <button
              key={post.id}
              disabled={scheduling}
              onClick={()=> onSchedule(post.id,date)}
            >
              <span>{post.headline}</span>
              <SafeIcon icon={FiChevronRight} />
            </button>
          )) : (
            <div className="calendar-empty">
              No approved posts are waiting to be scheduled.
            </div>
          )}
        </div>
        <button className="text-button" disabled={scheduling} onClick={onClose}>
          Cancel
        </button>
      </motion.div>
    </div>
  );
}

export default CalendarScreen;
